<template>
  <!-- View mode: plain, read-only rows -->
  <div v-if="mode === 'view'" class="seva seva--view">
    <div v-if="entry.values.length === 0" class="seva-empty">—</div>
    <div v-for="val in entry.values" :key="val.id" class="seva-view-row">
      <CustomFieldValueDisplay
        :fieldType="entry.field_type ?? 'text'"
        :passwordMode="passwordModeOf(entry.type_config)"
        :typeConfig="entry.type_config ?? undefined"
        :value="val.value"
      />
    </div>
  </div>

  <!-- Edit mode -->
  <div v-else class="seva">
    <!-- Single-value (or non-multi) entry -->
    <template v-if="!entry.is_multi">
      <CustomFieldValueInput
        :fieldType="entry.field_type ?? 'text'"
        :passwordMode="passwordModeOf(entry.type_config)"
        :typeConfig="entry.type_config ?? undefined"
        :modelValue="draft[singleKey()]"
        @update:modelValue="draft[singleKey()] = $event"
      />
    </template>

    <!-- Multi-value entry -->
    <template v-else>
      <div v-for="val in entry.values" :key="val.id" class="seva-multi-row">
        <div class="seva-value-row">
          <CustomFieldValueInput
            :fieldType="entry.field_type ?? 'text'"
            :passwordMode="passwordModeOf(entry.type_config)"
            :typeConfig="entry.type_config ?? undefined"
            :modelValue="draft[valueKey(val.id)]"
            @update:modelValue="draft[valueKey(val.id)] = $event"
          />
          <ion-button
            fill="clear"
            size="small"
            color="danger"
            @click="deleteValue(val.id)"
          >
            <ion-icon slot="icon-only" :icon="trashOutline" />
          </ion-button>
        </div>
      </div>

      <!-- Add value row -->
      <template v-if="addingValue">
        <CustomFieldValueInput
          :fieldType="entry.field_type ?? 'text'"
          :passwordMode="passwordModeOf(entry.type_config)"
          :typeConfig="entry.type_config ?? undefined"
          :modelValue="newValue"
          @update:modelValue="newValue = $event"
        />
        <div class="seva-edit-actions">
          <ion-button size="small" @click="saveNewValue">Add</ion-button>
          <ion-button size="small" fill="outline" @click="addingValue = false"
            >Cancel</ion-button
          >
        </div>
      </template>
      <ion-button
        v-else
        expand="block"
        fill="clear"
        size="small"
        @click="addingValue = true"
      >
        <ion-icon slot="start" :icon="addOutline" />
        Add value
      </ion-button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { IonButton, IonIcon } from "@ionic/vue";
import { addOutline, trashOutline } from "ionicons/icons";
import type {
  CustomSchemaEntryOut,
  CustomSchemaValueOut,
} from "@/models/custom-schemas";
import { useApiStore } from "@/stores/api";
import { passwordModeOf } from "@/helper/customFields";
import { isSecretFieldType } from "@/helper/drafts";
import CustomFieldValueDisplay from "@/components/CustomFieldValueDisplay.vue";
import CustomFieldValueInput from "@/components/CustomFieldValueInput.vue";

const props = withDefaults(
  defineProps<{
    entry: CustomSchemaEntryOut;
    objectId: number;
    mode?: "view" | "edit";
  }>(),
  {
    mode: "edit",
  }
);

const emit = defineEmits<{ refresh: [] }>();

const api = useApiStore();

const addingValue = ref(false);
const newValue = ref<any>(null);

// Draft/baseline value tracking for edit mode, keyed by real value id (existing
// values) or a synthetic "entry:<id>:new" key (single-value entry with no value yet).
const baseline = reactive<Record<string, any>>({});
const draft = reactive<Record<string, any>>({});

function singleKey(): string {
  const existing = props.entry.values[0];
  return existing ? valueKey(existing.id) : `entry:${props.entry.id}:new`;
}

function valueKey(valueId: CustomSchemaValueOut["id"]): string {
  return `value:${valueId}`;
}

function seedFromEntry(entry: CustomSchemaEntryOut) {
  const seenKeys = new Set<string>();
  if (!entry.is_multi) {
    const key = singleKey();
    seenKeys.add(key);
    if (!(key in baseline)) {
      const value = entry.values[0]?.value ?? null;
      baseline[key] = value;
      draft[key] = value;
    }
  } else {
    for (const val of entry.values) {
      const key = valueKey(val.id);
      seenKeys.add(key);
      if (!(key in baseline)) {
        baseline[key] = val.value;
        draft[key] = val.value;
      }
    }
  }
  for (const key of Object.keys(baseline)) {
    if (!seenKeys.has(key)) {
      delete baseline[key];
      delete draft[key];
    }
  }
}

watch(
  () => props.entry,
  (entry) => seedFromEntry(entry),
  { deep: true, immediate: true }
);

async function saveNewValue() {
  await api.addSchemaFieldValue({
    objectId: props.objectId,
    entryId: props.entry.id,
    value: newValue.value,
  });
  addingValue.value = false;
  newValue.value = null;
  emit("refresh");
}

async function deleteValue(valueId: number) {
  await api.deleteSchemaFieldValue({
    objectId: props.objectId,
    entryId: props.entry.id,
    valueId,
  });
  emit("refresh");
}

async function flush() {
  if (!props.entry.is_multi) {
    const key = singleKey();
    if (draft[key] !== baseline[key]) {
      const value = draft[key];
      await api.setSchemaFieldValue({
        objectId: props.objectId,
        entryId: props.entry.id,
        value,
      });
      baseline[key] = value;
      emit("refresh");
    }
    return;
  }
  for (const val of props.entry.values) {
    const key = valueKey(val.id);
    if (draft[key] === baseline[key]) continue;
    const value = draft[key];
    // No dedicated update endpoint for multi-values; replace via delete + add.
    await api.deleteSchemaFieldValue({
      objectId: props.objectId,
      entryId: props.entry.id,
      valueId: val.id,
    });
    await api.addSchemaFieldValue({
      objectId: props.objectId,
      entryId: props.entry.id,
      value,
    });
    baseline[key] = value;
    emit("refresh");
  }
}

function reset() {
  for (const key of Object.keys(baseline)) {
    draft[key] = baseline[key];
  }
}

/** The edited values, for the draft of the dialog. Values of password entries are never part of it. */
function getDraft(): Record<string, any> {
  if (isSecretFieldType(props.entry.field_type)) return {};
  const edited: Record<string, any> = {};
  for (const key of Object.keys(draft)) {
    if (draft[key] !== baseline[key]) edited[key] = draft[key];
  }
  return edited;
}

/** Puts values of a saved draft back, skipping values that do not exist anymore. */
function applyDraft(values: Record<string, any>) {
  if (isSecretFieldType(props.entry.field_type)) return;
  for (const [key, value] of Object.entries(values)) {
    if (key in baseline) draft[key] = value;
  }
}

defineExpose({ flush, reset, getDraft, applyDraft });
</script>

<style scoped>
.seva {
  width: 100%;
}

.seva-value-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.seva-empty {
  font-size: 0.85em;
  color: var(--ion-color-medium);
  font-style: italic;
}

.seva-edit-actions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.seva-multi-row {
  margin-bottom: 4px;
}

/* View mode */
.seva--view {
  width: 100%;
}

.seva-view-row {
  padding: 2px 0;
}
</style>
